// The ConsultationPackage (design 8.1; research 03 section 5, Claude's draft).
//
// The consult's output is a MAP of the business: facts keyed by the active
// InfoMap's keys, each with verbatim quotes that cite transcript message
// indexes, plus lists (tools, pains, quantities, people), the read-back,
// flags, consent, versions, the cost meter and the test flag. It is stored in
// our own database (index in D1, body in R2) and POSTed to a configurable
// webhook only when HANDOFF_ENABLED is on (Wes, Sept 28 2026).
//
// No workflow fields, ever: no workflow id, name, score or catalog item
// (the Sept 25 2026 split). Objects are strict, so an unknown field such as
// "workflow_id" fails validation instead of riding along.
//
// Zod only: this file set can be handed to the engine as is.
import { z } from "zod";

export const PACKAGE_VERSION = "otto-package@1" as const;

const IsoTime = z.iso.datetime({ offset: true });
const Uuid = z.uuid();
/** Fact and area keys follow the InfoMap id rule. */
const Key = z.string().regex(/^[a-z][a-z0-9_]{0,63}$/);
const DASHES = /[\u2013\u2014]/;
/** Text the engine may show a prospect: no em or en dashes (house rule). */
const CleanText = (max: number) =>
  z
    .string()
    .max(max)
    .refine((s) => !DASHES.test(s), { message: "No em or en dashes" });

// ---------------------------------------------------------------------------
// Evidence and the transcript
// ---------------------------------------------------------------------------

/** A verbatim quote from one transcript message, cited by index. */
export const Evidence = z.strictObject({
  /** transcript.messages[].i */
  i: z.number().int().min(0),
  /** Verbatim, a whole sentence where possible (research 03 section 2.5). */
  quote: z.string().min(1).max(1000),
});
export type Evidence = z.infer<typeof Evidence>;

export const Speaker = z.enum(["otto", "owner"]);

export const TranscriptMessage = z.strictObject({
  i: z.number().int().min(0),
  role: Speaker,
  /** Punctuated; report quotes need sentence punctuation (research 03 section 2.5). */
  text: z.string().max(20_000),
  start: IsoTime,
  end: IsoTime,
});
export type TranscriptMessage = z.infer<typeof TranscriptMessage>;

export const Transcript = z.strictObject({
  text_source: z.enum(["voice_engine_transcription", "cascade_asr"]),
  punctuated: z.boolean(),
  messages: z.array(TranscriptMessage),
});

// ---------------------------------------------------------------------------
// Facts and lists
// ---------------------------------------------------------------------------

export const FactStatus = z.enum(["captured", "negative", "unclear", "declined", "not_applicable", "not_asked"]);
export type FactStatus = z.infer<typeof FactStatus>;

export const Fact = z
  .strictObject({
    /** An InfoMap fact key from the pinned info_map version. */
    key: Key,
    status: FactStatus,
    /** What the engine reads: 240 characters or less (the engine's limit, research 03 section 1). */
    answer: CleanText(240).nullable(),
    /** For "list" facts: one item per entry, so nothing is cut at 240 characters. */
    items: z.array(CleanText(240)).optional(),
    evidence: z.array(Evidence),
    confidence: z.enum(["stated", "inferred"]),
    /** Whether Otto asked for it directly. */
    asked: z.boolean(),
    /** Spanish consults: the owner's words stay in evidence; answer is the English normalization. */
    original_language: z.string().min(2).max(35).optional(),
    note: z.string().max(500).nullable().optional(),
  })
  .superRefine((fact, ctx) => {
    if ((fact.status === "captured" || fact.status === "negative") && fact.evidence.length === 0) {
      ctx.addIssue({ code: "custom", path: ["evidence"], message: "A captured or negative fact needs at least one quote" });
    }
  });
export type Fact = z.infer<typeof Fact>;

export const Period = z.enum(["day", "week", "month", "quarter", "year", "job", "customer", "other"]);

export const Quantity = z.strictObject({
  /** The InfoMap fact this number belongs to, if any. */
  key: Key.nullable(),
  value: z.number().nullable(),
  low: z.number().nullable(),
  high: z.number().nullable(),
  unit: z.string().max(60),
  per: Period.nullable(),
  approx: z.boolean(),
  /** The owner's own words; numbers are kept exactly as said. */
  said: z.string().max(500),
  evidence: z.array(Evidence).min(1),
});
export type Quantity = z.infer<typeof Quantity>;

export const Tool = z.strictObject({
  category: z.enum([
    "crm",
    "email_calendar",
    "accounting",
    "job_management",
    "booking",
    "phones_texting",
    "website_forms",
    "reviews_profile",
    "spreadsheet",
    "paper",
    "other",
  ]),
  name: z.string().min(1).max(120),
  plan_or_edition: z.string().max(120).nullable(),
  used_for: CleanText(240).nullable(),
  who_uses: z.string().max(120).nullable(),
  holds_records: z.array(z.string().max(60)),
  automations_on: CleanText(240).nullable(),
  /** 27 of 40 engine disqualifiers turn on satisfaction (research 03 section 3.2). */
  satisfaction: z.enum(["satisfied", "mixed", "unhappy", "unknown"]),
  evidence: z.array(Evidence).min(1),
});
export type Tool = z.infer<typeof Tool>;

export const Pain = z.strictObject({
  summary: CleanText(240),
  /** An InfoMap area id. */
  area: Key,
  who_handles: z.string().max(120).nullable(),
  tool: z.string().max(120).nullable(),
  how_often: z.string().max(120).nullable(),
  time_cost: z
    .strictObject({
      value: z.number().nullable(),
      unit: z.string().max(40),
      per: Period.nullable(),
      said: z.string().max(500),
    })
    .nullable(),
  /** Only if the owner said an amount. */
  money_cost_said: z.string().max(500).nullable(),
  is_biggest: z.boolean(),
  evidence: z.array(Evidence).min(1),
});
export type Pain = z.infer<typeof Pain>;

export const People = z.strictObject({
  /** One number for the report cover; the engine cannot print a range. */
  headcount: z
    .strictObject({
      value: z.number().int().min(1),
      said: z.string().max(200),
      includes_owner: z.boolean(),
      evidence: z.array(Evidence).min(1),
    })
    .nullable(),
  roles: z.array(
    z.strictObject({
      role: z.string().min(1).max(120),
      count: z.number().int().min(0).nullable(),
      does: CleanText(240).nullable(),
      evidence: z.array(Evidence),
    }),
  ),
});

const Beat = z.strictObject({ answer: CleanText(500).nullable(), evidence: z.array(Evidence) });

/** The three non-price beats. There is no price beat (research 03 section 5.6 item 10). */
export const Priorities = z.strictObject({
  top_goals: z.array(z.strictObject({ goal: CleanText(240), evidence: z.array(Evidence).min(1) })),
  unprompted_wishlist: Beat,
  informed_wishlist: z.strictObject({
    first: CleanText(240).nullable(),
    second: CleanText(240).nullable(),
    evidence: z.array(Evidence),
  }),
  six_months: Beat,
});

// ---------------------------------------------------------------------------
// Read-back, coverage, flags
// ---------------------------------------------------------------------------

export const ReadBack = z
  .strictObject({
    /** The approved draft Otto was given (checked by the guard before speech). */
    text: CleanText(1000),
    source: z.enum(["drafter", "code_fallback"]),
    /** Index of the Otto message where he said it, if he did. */
    spoken_i: z.number().int().min(0).nullable(),
    /** The guard's after-speech check of his words against the draft (design 5.5). null if not checked. */
    spoken_matches_draft: z.boolean().nullable(),
    owner_response: z.enum(["confirmed", "corrected", "no_answer"]),
    corrections: z.array(
      z.strictObject({
        key: Key.nullable(),
        evidence: z.array(Evidence).min(1),
      }),
    ),
  })
  .nullable();

export const AreaCoverage = z.enum(["covered", "touched", "untouched", "not_applicable"]);

export const Coverage = z.strictObject({
  /** InfoMap area id to coverage. */
  areas: z.record(Key, AreaCoverage),
  must_total: z.number().int().min(0),
  /** MUST facts captured or explicitly negative. */
  must_resolved: z.number().int().min(0),
  open_gaps: z.array(
    z.strictObject({
      key: Key,
      reason: z.enum(["ran_out_of_time", "declined", "unclear", "not_applicable", "not_asked"]),
    }),
  ),
});

export const HardGateFailure = z.strictObject({
  /** ConductRules rule id. */
  rule_id: Key,
  /** Transcript index where it happened, if it maps to one. */
  i: z.number().int().min(0).nullable(),
  detail: z.string().max(500),
});

export const Flags = z.strictObject({
  asked_about_price: z.boolean(),
  asked_for_human: z.boolean(),
  stressed: z.boolean(),
  skeptical: z.boolean(),
  abusive: z.boolean(),
  prankster: z.boolean(),
  not_a_real_consult: z.boolean(),
  volunteered_personal_details: z.boolean(),
  /** Passages masked as "[details removed]" in every copy that leaves the consult (design 9.3). */
  masked_passages: z.number().int().min(0),
  hard_gate_failures: z.array(HardGateFailure),
});

// ---------------------------------------------------------------------------
// Identity, source, consent, contact, session, versions, meter
// ---------------------------------------------------------------------------

export const EndReason = z.enum([
  "completed",
  "owner_ended",
  "time_limit",
  "silence_timeout",
  "connection_lost",
  "withdrew_consent",
  "conduct",
  "spend_cap",
  // An admin ended the call from Live now (integration, Sept 28 2026). Mirrors @otto/protocol EndReason.
  "admin_ended",
  "error",
]);

export const TestKind = z.enum(["canary", "golden_set", "full_audio", "try_it", "wes_test", "load_test"]);

const Money = z.number().min(0).nullable(); // null = not priced (never shown as $0)

export const ConsultationPackage = z
  .strictObject({
    package_version: z.literal(PACKAGE_VERSION),
    /** The idempotency key: the same value on every retry of this handoff. */
    package_id: Uuid,
    /** The one id everything carries (design 8.1). */
    consult_id: Uuid,
    continues_consult_id: Uuid.nullable(),
    previous_consult_id: Uuid.nullable(),
    created_at: IsoTime,
    /** Tests never reach an email, a CRM or a metric (design 8.1). */
    is_test: z.boolean(),
    test_kind: TestKind.nullable(),

    source: z.strictObject({
      surface: z.enum(["website_consult", "booth", "partner_link", "other"]),
      channel: Key,
      channel_version: z.number().int().min(1).nullable(),
      channel_chosen_by: z.enum(["link", "checkin", "detected", "default"]),
      source_code: z.string().max(64).nullable(),
      utm: z.record(z.string().max(40), z.string().max(200)),
      referrer: z.string().max(2000).nullable(),
      landing_url: z.string().max(2000).nullable(),
    }),

    mode: z.strictObject({
      chosen_at_checkin: z.enum(["video", "voice"]),
      final: z.enum(["video", "voice"]),
      toggles: z.number().int().min(0),
    }),

    language: z.strictObject({
      chosen: z.enum(["en", "es"]),
      /** BCP 47 tags heard in the call. */
      spoken: z.array(z.string().min(2).max(35)),
      /** The language the report should be written in. */
      report: z.string().min(2).max(35),
    }),

    consent: z.strictObject({
      notice_version: z.string().min(1).max(64),
      notice_shown_at: IsoTime,
      transcript_agreed: z.literal(true),
      adult_and_business_attested: z.boolean(),
      /** Otto said he takes notes. */
      notes_disclosed: z.boolean(),
      /** The written AI disclosure was shown, and in voice only the robot line was spoken. */
      otto_disclosed_ai: z.boolean(),
      participants: z.number().int().min(1).max(2),
      host_confirmed_consent: z.boolean().nullable(),
    }),

    contact: z.strictObject({
      full_name: z.string().min(1).max(200),
      first_name: z.string().min(1).max(100),
      last_name: z.string().max(100).nullable(),
      /** Lowercased, exactly as typed otherwise. */
      email: z.email().max(320),
      phone_e164: z.string().regex(/^\+[1-9]\d{6,14}$/),
      business_name: z.string().min(1).max(200),
      industry: z.strictObject({
        pick: z.string().max(80).nullable(),
        /** Free text when the pick is "Other". */
        other_text: z.string().max(200).nullable(),
      }),
      role_title: z.string().max(120).nullable(),
      decides_on_purchases: z.enum(["yes", "shares_decision", "no", "unknown"]),
    }),

    session: z.strictObject({
      started_at: IsoTime,
      ended_at: IsoTime,
      /** Connected seconds (the clock). */
      connected_seconds: z.number().int().min(0),
      /** Seconds the owner was actually talking. */
      owner_talk_seconds: z.number().int().min(0),
      end_reason: EndReason,
      owner_turns: z.number().int().min(0),
      otto_turns: z.number().int().min(0),
      extension: z.strictObject({ offered: z.boolean(), accepted: z.boolean().nullable() }),
      reconnects: z.number().int().min(0),
      session_swaps: z.number().int().min(0),
    }),

    /** Every version Otto ran with (design 9.11). */
    versions: z.strictObject({
      config: z.strictObject({
        brief: z.number().int().min(1),
        conduct: z.number().int().min(1),
        info_map: z.number().int().min(1),
        agenda: z.number().int().min(1),
        facts: z.number().int().min(1),
        timing: z.number().int().min(1),
        voice: z.number().int().min(1),
        coach: z.number().int().min(1),
      }),
      voice_model: z.string().min(1),
      voice_name: z.string().min(1),
      vad_profile: z.enum(["home", "field", "room"]),
      session_mode: z.enum(["rolling", "compression"]),
      coach_model: z.string().nullable(),
      drafter_model: z.string().nullable(),
      writer_model: z.string().nullable(),
      app_commit: z.string().max(64).nullable(),
      live_commit: z.string().max(64).nullable(),
    }),

    transcript: Transcript,
    facts: z.array(Fact),
    quantities: z.array(Quantity),
    tools: z.array(Tool),
    pains: z.array(Pain),
    people: People,
    priorities: Priorities,
    readback: ReadBack,
    coverage: Coverage,
    flags: Flags,

    /** Who wrote the facts (design 8.2 degraded paths). */
    extraction: z.enum(["writer", "writer_fallback", "ledger_only", "transcript_only"]),

    meter: z.strictObject({
      /** metered = from provider usage data; estimated = arithmetic. */
      basis: z.enum(["metered", "estimated"]),
      cost_usd: z.strictObject({
        voice: Money,
        coach: Money,
        drafter: Money,
        writer: Money,
        total: Money,
      }),
      tokens: z.record(z.string().max(60), z.number().int().min(0)),
      reply_latency_ms: z.strictObject({
        p50: z.number().min(0).nullable(),
        p90: z.number().min(0).nullable(),
        samples: z.number().int().min(0),
      }),
    }),

    /** Internal only, never shown to the prospect. */
    otto_notes: z.strictObject({
      prospect_questions: z.array(z.string().max(500)),
      tone: z.string().max(200).nullable(),
    }),
  })
  .superRefine((pkg, ctx) => {
    const byIndex = new Map(pkg.transcript.messages.map((m) => [m.i, m]));
    if (byIndex.size !== pkg.transcript.messages.length) {
      ctx.addIssue({ code: "custom", path: ["transcript", "messages"], message: "Duplicate message index" });
    }
    if (pkg.is_test !== (pkg.test_kind !== null)) {
      ctx.addIssue({ code: "custom", path: ["test_kind"], message: "test_kind is set exactly when is_test is true" });
    }
    // Every fact quote must cite an owner message that exists (the engine's
    // honesty rule). Quote containment is checked by checkEvidence().
    pkg.facts.forEach((fact, f) => {
      fact.evidence.forEach((ev, e) => {
        const message = byIndex.get(ev.i);
        if (!message) {
          ctx.addIssue({ code: "custom", path: ["facts", f, "evidence", e, "i"], message: `No transcript message ${ev.i}` });
        } else if (message.role !== "owner") {
          ctx.addIssue({ code: "custom", path: ["facts", f, "evidence", e, "i"], message: `Message ${ev.i} is Otto's, not the owner's` });
        }
      });
    });
  });
export type ConsultationPackage = z.infer<typeof ConsultationPackage>;

// ---------------------------------------------------------------------------
// The engine's honesty check, done on our side too (design 8.2 step 2)
// ---------------------------------------------------------------------------

/** Letters and digits only, case folded: the engine's comparison (research 03 section 2.5). */
export function normalizeForQuote(text: string): string {
  return text.toLowerCase().replace(/[^\p{L}\p{N}]+/gu, "");
}

export interface EvidenceProblem {
  path: string;
  problem: "missing_message" | "not_owner" | "quote_not_found";
}

/**
 * Every quote anywhere in the package must appear in the message it cites.
 * The package builder turns a failing fact into "unclear" with a note; this
 * function finds them.
 */
export function checkEvidence(pkg: ConsultationPackage): EvidenceProblem[] {
  const byIndex = new Map(pkg.transcript.messages.map((m) => [m.i, m]));
  const problems: EvidenceProblem[] = [];
  const visit = (evidence: readonly Evidence[], path: string) => {
    evidence.forEach((ev, n) => {
      const message = byIndex.get(ev.i);
      const at = `${path}[${n}]`;
      if (!message) problems.push({ path: at, problem: "missing_message" });
      else if (message.role !== "owner") problems.push({ path: at, problem: "not_owner" });
      else if (!normalizeForQuote(message.text).includes(normalizeForQuote(ev.quote))) {
        problems.push({ path: at, problem: "quote_not_found" });
      }
    });
  };
  pkg.facts.forEach((f, n) => visit(f.evidence, `facts[${n}].evidence`));
  pkg.quantities.forEach((q, n) => visit(q.evidence, `quantities[${n}].evidence`));
  pkg.tools.forEach((t, n) => visit(t.evidence, `tools[${n}].evidence`));
  pkg.pains.forEach((p, n) => visit(p.evidence, `pains[${n}].evidence`));
  return problems;
}
